import { useEffect, useState, useContext, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { openCheckout } from '../services/nativeBrowser';
import { db, auth } from '../firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import axios from 'axios';
import { UserContext } from '../contexts/UserContext';
import { MembershipContext } from '../contexts/MembershipContext';
import { useContact } from '../contexts/ContactContext';
import { getInitiatorPedalIds, getUserReceivesCash, getNetCashReceived, userNeedsPayoutOnboarding } from '../utils/tradeUtils';
import PayoutAccountLinkedModal from '../components/PayoutAccountLinkedModal';
import SavingsBanner from '../components/SavingsBanner';
import { marketplaceFeeSavingsDollars } from '../utils/marketplaceSavings';

const TradeConfirm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { firestoreUser, loading: userDataLoading, refreshUserData } = useContext(UserContext);
  const { getCashFeePercentage, getTradeCompletionFee, getTradeProtectFee } = useContext(MembershipContext);
  const { openContact } = useContact();
  
  // State declarations MUST come first
  const [trade, setTrade] = useState(null);
  // { reconnected, onContinue } — alreadyOnboarded notice; navigation runs on
  // dismiss (mirrors SellerPurchaseConfirm).
  const [payoutLinkedNotice, setPayoutLinkedNotice] = useState(null);
  const [loading, setLoading] = useState(true);
  // Auth resolution + confirmed-missing flags so we never flash "Trade not found"
  // to a logged-in user during load / pull-to-refresh (which does a full reload).
  const [currentUser, setCurrentUser] = useState(auth.currentUser);
  const [authChecked, setAuthChecked] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [includeProtection, setIncludeProtection] = useState(false);
  const [applyCashCredit, setApplyCashCredit] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [usernames, setUsernames] = useState({ fromUser: '', toUser: '' });
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
  // One-shot guard for the default-ON persist below — must never re-fire and
  // never overwrite a real choice (see the effect for the full contract).
  const defaultProtectionAttemptedRef = useRef(false);
  // True once the user has touched the toggle themselves. The default
  // effect's late completion must never overwrite a human choice (a fast
  // toggle-off during the in-flight default write raced its setState and
  // flipped the UI back ON — review finding, 2026-09-18).
  const userToggledProtectionRef = useRef(false);
  // False until the rehydrate/default effect has resolved the toggle's initial
  // position. The switch renders WITHOUT its slide transition until then, so
  // the default-ON state appears in place instead of visibly flicking on a
  // beat after page load (Jason, 2026-09-18).
  const [protectionHydrated, setProtectionHydrated] = useState(false);
  
  // Clean up stale session storage on mount (shared with Stripe Connect onboarding flow)
  useEffect(() => {
    const returnUrl = sessionStorage.getItem('returnToTradeConfirm');
    if (returnUrl) {
      sessionStorage.removeItem('returnToTradeConfirm');
    }
  }, []);

  // Check if user is returning from Stripe onboarding and should go to settlement instead.
  // Net-based: only auto-advance if the user will actually NET a payout (a receiver
  // whose cash is fully eaten by fees nets $0 and should stay here to complete
  // payment). NOTE: net is computed INLINE here on purpose — this effect runs near
  // the top of the component, before the render-scope `netCashReceived` const exists,
  // so referencing that outer const (even in the dep array) throws a TDZ
  // ReferenceError and white-screens the page.
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const isOnboardingReturn = urlParams.has('onboarding') || urlParams.has('stripe_return');
    if (!isOnboardingReturn || !trade || !auth.currentUser) return;

    const uid = auth.currentUser.uid;
    const cashAmt = Number(trade.cashAmount) || 0;
    const grossReceived = getUserReceivesCash(trade, uid) ? cashAmt : 0;
    const isInitiator = uid === trade.fromUserId;
    let pFee = getTradeCompletionFee(isInitiator ? getInitiatorPedalIds(trade).length : 1);
    if (pFee > 0 && (firestoreUser?.referralCredits || 0) > 0) pFee = 0;
    // Price from the PERSISTED doc flag, not local toggle state: on a fresh
    // onboarding return this effect can run before the rehydrate/default
    // effect has set the state, and understating fees here misroutes a
    // protected receiver to settlement. `undefined` counts as protected —
    // that is what the default-ON persist is about to write (2026-09-18).
    const obUserKey = isInitiator ? 'fromUser' : 'toUser';
    const docProtected = trade.protection?.[obUserKey] !== false;
    const fees = pFee + (docProtected ? getTradeProtectFee() : 0);
    const net = getNetCashReceived({ grossReceived, totalFees: fees, applyCashCredit });
    if (net > 0) {
      console.log('🚀 User nets a payout and returned from onboarding, redirecting to settlement...');
      navigate(`/trade-settlement/${trade.id}?onboarding=return`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trade, navigate, firestoreUser, includeProtection, applyCashCredit, getTradeCompletionFee, getTradeProtectFee]);

  useEffect(() => {
    const fetchTrade = async () => {
      try {
        const docRef = doc(db, 'tradeOffers', id);
        const snap = await getDoc(docRef);
        if (snap.exists()) {
          const tradeData = { id: snap.id, ...snap.data() };
          setNotFound(false);
          setTrade(tradeData);
          
          // Protection state is owned by the rehydrate/default-ON effect
          // below (2026-09-18). This supersedes the 2025-08-07 "always start
          // unchecked" fix (633f448): that bug class is prevented now by the
          // doc flag being the single source of truth on every mount, a saved
          // `false` being sacred, and nothing gating Continue on protection —
          // see TRADEPROTECT_REPRICE_PLAN.md, "onboarding-return contract".

          // Fetch usernames for both users
          const fromUserRef = doc(db, 'publicProfiles', tradeData.fromUserId);
          const toUserRef = doc(db, 'publicProfiles', tradeData.toUserId);

          const [fromSnap, toSnap] = await Promise.all([
            getDoc(fromUserRef),
            getDoc(toUserRef)
          ]);

          const fromUsername = fromSnap.exists() ? fromSnap.data().username || 'User A' : 'User A';
          const toUsername = toSnap.exists() ? toSnap.data().username || 'User B' : 'User B';

          setUsernames({ fromUser: fromUsername, toUser: toUsername });
        } else {
          setNotFound(true);
        }
      } catch (err) {
        console.error('Error fetching trade:', err);
        // A failed load (no access / network) surfaces the friendly terminal
        // state rather than spinning on "Loading" forever.
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    // Wait for auth to resolve before fetching/deciding. On a fresh load or
    // pull-to-refresh reload, auth.currentUser is briefly null; driving off
    // onAuthStateChanged means a logged-in user stays on the loading state until
    // data arrives instead of flashing "Please log in" / "Trade not found".
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setAuthChecked(true);
      if (user) {
        fetchTrade();
      } else {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [id]);

  const checkOnboardingAndProceed = async () => {
    setProcessingPayment(true);
    try {
      const userId = auth.currentUser?.uid;
      if (!userId) throw new Error('Authentication required');

      // ✅ CRITICAL FIX: Ensure user data is loaded before proceeding
      if (userDataLoading) {
        console.log('⏳ User data still loading, blocking proceed');
        alert('⏳ Please wait while we load your account information...');
        return;
      }
      
      // Gate onboarding on NET payout, not gross "is a receiver". `netCashReceived`
      // is the shared value (tradeUtils) that also drives the button label/panel
      // below, so the prompt and the redirect can never disagree. If the user's
      // received cash is fully eaten by fees (net $0), they pocket nothing and
      // don't need a payout account — skip straight to payment.
      if (netCashReceived > 0) {
        const userRef = doc(db, 'users', userId);
        const userSnap = await getDoc(userRef);
        // Gate on COMPLETION, not mere account existence. A dismissed onboarding
        // attempt leaves a stripeAccountId behind WITHOUT stripeOnboardingComplete;
        // keying off the id alone let users skip setup and get marked paid.
        const onboarded = userSnap.exists() && !!userSnap.data().stripeOnboardingComplete;
        if (!onboarded) {
          // User hasn't finished payout onboarding - (re)start Stripe Connect.
          console.log('🔄 User needs Stripe onboarding, redirecting...');
          await redirectToStripeOnboarding();
          return;
        }
        // Onboarded NET RECEIVER → settlement. A receiver must NEVER go through the
        // payment/referral checkout path: they don't owe money (their fee is netted
        // from the cash they receive), so handleProceedToPayment would wrongly
        // consume a referral credit and mark them "payment complete" via the payer
        // flow — dumping them on trade-progress instead of letting them settle.
        // Settlement marks them paid; the payout flows via escrow.
        console.log('✅ Net receiver onboarded — proceeding to settlement');
        navigate(`/trade-settlement/${trade.id}`);
        return;
      }

      // NOT a net receiver (payer, or net-zero whose cash didn't cover fees):
      // they owe fees, or a referral credit covers the fees they owe → payment /
      // referral path. Otherwise nothing is owed → settle.
      if (finalAmount > 0 || hasReferralCredit) {
        // If finalAmount > 0: normal Stripe checkout
        // If finalAmount === 0 + referral credit: backend handles credit decrement and marks as paid
        await handleProceedToPayment();
      } else {
        // No payment required - go to settlement page to mark as paid
        navigate(`/trade-settlement/${trade.id}`);
      }
    } catch (err) {
      console.error('❌ Error in onboarding check:', err);
      alert(`Error: ${err.message}`);
    } finally {
      setProcessingPayment(false);
    }
  };

  const redirectToStripeOnboarding = async () => {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('No logged-in user.');
      
      const idToken = await user.getIdToken();
      const baseUrl = 'https://us-central1-pedalxchange.cloudfunctions.net';
      
      // Send to settlement after onboarding only if the user will actually NET cash.
      // Aligned to the SAME shared `netCashReceived` that gates this call and drives
      // the button/panel — not a separate gross "is a receiver" recompute — so the
      // return target can't disagree with the gate. (This function is only reached
      // when netCashReceived > 0, so this is true in every reachable case; keeping it
      // net-based removes the last gross check rather than leaving a known inconsistency.)
      const userReceivesPayout = netCashReceived > 0;

      // Determine return URL based on whether user nets money
      const returnUrl = userReceivesPayout 
        ? `${window.location.origin}/trade-settlement/${id}?onboarding=return`
        : window.location.href;
        
      console.log('🔄 Stripe onboarding returnUrl:', returnUrl, 'userReceivesPayout:', userReceivesPayout);
        
      const response = await axios.post(
        `${baseUrl}/createStripeConnectAccount`,
        { userId: user.uid, tradeId: id, returnUrl, returnTo: Capacitor.isNativePlatform() ? 'app' : undefined },
        { headers: { Authorization: `Bearer ${idToken}` } }
      );
      
      if (response.data.onboardingUrl) {
        // Store current page in sessionStorage for redirect back
        sessionStorage.setItem('returnToTradeConfirm', window.location.href);

        // Native: in-app browser sheet; on dismiss, head to settlement. `onReturn`
        // ONLY runs on native — on web openCheckout does a full-page redirect and
        // Stripe's own return_url (which already carries onboarding=return +
        // stripeAccountId) brings the user back. On native that return_url is the
        // in-app handoff page instead, so we must re-attach the SAME reconcile
        // params here from the account id the function just returned. Without them
        // checkStripeAccountStatus never runs and the new account is never saved.
        // (The account.updated webhook is the server-side backstop for the cases
        // where the user never taps back in.)
        const acctId = response.data.stripeAccountId;
        const settlementUrl = acctId
          ? `/trade-settlement/${id}?onboarding=return&stripeAccountId=${acctId}`
          : `/trade-settlement/${id}?onboarding=return`;
        // `onReturn` fires on ANY sheet dismissal — including tapping the X to back
        // out. So re-verify with Stripe and ONLY advance to settlement if payout
        // onboarding actually completed; otherwise stay put (the info panel +
        // "Set up payouts" button let the user retry). Native-only path; web does a
        // full-page redirect handled by Stripe's own return_url.
        await openCheckout(response.data.onboardingUrl, {
          onReturn: async () => {
            // POLL, don't check once: on a brand-new account Stripe often flips
            // the `transfers` capability to active a few seconds AFTER the user
            // finishes onboarding, so a single immediate check sees "not ready",
            // declines to advance, and strands a successfully-onboarded user on
            // this page with one extra tap. Mirrors the F7 payment polling.
            let complete = false;
            for (let attempt = 0; attempt < 6 && !complete; attempt++) {
              if (attempt > 0) await new Promise(r => setTimeout(r, 2500));
              try {
                await axios.post(
                  `${baseUrl}/checkStripeAccountStatus`,
                  { userId: user.uid, stripeAccountId: acctId || undefined },
                  { headers: { Authorization: `Bearer ${idToken}` } }
                );
                // Read the doc rather than the response so the account.updated
                // webhook backstop also counts if it lands first.
                const fresh = await getDoc(doc(db, 'users', user.uid));
                complete = fresh.exists() && !!fresh.data().stripeOnboardingComplete;
              } catch (e) {
                console.warn('Payout status re-check failed after onboarding sheet closed:', e?.message || e);
              }
            }
            // Refresh context so the button/info panel reflect the new state if they stay.
            if (refreshUserData) {
              try { await refreshUserData(); } catch { /* non-fatal */ }
            }
            if (complete) navigate(settlementUrl);
          }
        });
      } else if (response.data.alreadyOnboarded) {
        // Stripe says the account is already fully onboarded (our Firestore flag was
        // just stale). This function is only ever reached for a NET RECEIVER, so send
        // them to settlement — NOT handleProceedToPayment, which would route a
        // receiver through the payer/referral checkout (consuming a credit + marking
        // them paid via the wrong flow). Mirrors SellerPurchaseConfirm.
        // Pass ?onboarding=return so settlement runs its authoritative Stripe
        // recheck (recheckPayoutStatus) instead of trusting a possibly-stale
        // payoutReady=false — otherwise the "Set up payout" gate flashes for a
        // user who is in fact fully onboarded.
        console.log('✅ Account already onboarded — proceeding to settlement');
        setPayoutLinkedNotice({
          reconnected: !!response.data.reconnected,
          onContinue: () => navigate(`/trade-settlement/${trade.id}?onboarding=return`),
        });
      } else {
        throw new Error('Failed to get onboarding URL');
      }
    } catch (err) {
      console.error('❌ Error starting onboarding:', err);
      alert(`Failed to start payout setup: ${err.message}`);
    }
  };

  // Rehydrate the saved choice on every mount; persist the ON default once
  // for a truly undecided doc. The contract (2026-09-18, replaces the 2025-08
  // accept/decline buttons — full reasoning in TRADEPROTECT_REPRICE_PLAN.md):
  //  - the doc flag is the single source of truth: what this page displays is
  //    exactly what settleTrade reads (trade.protection[userKey] === true);
  //  - a saved `false` is sacred — no return path ever flips it back on;
  //  - the default is persisted BEFORE it is shown: if the write fails, the
  //    toggle renders OFF (fail-safe: not charged) and Continue still works.
  useEffect(() => {
    if (!trade || !currentUser) return;
    // PARTY CHECK FIRST (Jason's spec, 2026-09-18): identity is verified
    // before any key is derived or any write scheduled. Without this, an
    // admin (whom the rules treat as a trade party) opening the page to
    // inspect a trade fell through to userKey='toUser' and silently enrolled
    // the OTHER user in TradeProtect. Non-parties get the read-only view
    // (switch disabled below) and this effect makes ZERO writes — proven by
    // the non-party test in tradeProtectDefaultOn.test.js.
    const isFrom = currentUser.uid === trade.fromUserId;
    const isTo = currentUser.uid === trade.toUserId;
    if (!isFrom && !isTo) {
      const viewerKey = 'fromUser'; // arbitrary: display-only for non-parties
      setIncludeProtection(trade.protection?.[viewerKey] === true);
      setProtectionHydrated(true);
      return;
    }
    const userKey = isFrom ? 'fromUser' : 'toUser';
    const saved = trade.protection?.[userKey];
    if (typeof saved === 'boolean') {
      setIncludeProtection(saved);
      setProtectionHydrated(true);
      return;
    }
    const alreadyPaid = isFrom
      ? trade.paymentStatus?.fromUserPaid
      : trade.paymentStatus?.toUserPaid;
    if (alreadyPaid || defaultProtectionAttemptedRef.current) {
      setProtectionHydrated(true);
      return;
    }
    defaultProtectionAttemptedRef.current = true;
    (async () => {
      try {
        await updateDoc(doc(db, 'tradeOffers', trade.id), {
          [`protection.${userKey}`]: true,
          // Analytics marker: defaulted, not chosen. Cleared on any explicit
          // toggle so adoption metrics can separate the two. Read by no money
          // path (settlement checks only protection.<key> === true).
          [`protectionDefaulted.${userKey}`]: true,
          updatedAt: new Date()
        });
        // Keep the local snapshot in step with what we just wrote so every
        // later consumer (onboarding-return pricing) reads current truth.
        setTrade((prev) => prev ? {
          ...prev,
          protection: { ...(prev.protection || {}), [userKey]: true }
        } : prev);
        // A human toggle during the write wins over the default's completion.
        if (!userToggledProtectionRef.current) setIncludeProtection(true);
      } catch (err) {
        console.error('❌ Could not persist the default TradeProtect choice (leaving it OFF):', err);
      } finally {
        setProtectionHydrated(true);
      }
    })();
  }, [trade, currentUser]);

  const handleProtectionToggle = async (checked) => {
    if (!trade || !auth.currentUser) return;
    const userId = auth.currentUser.uid;
    const isFromUser = userId === trade.fromUserId;
    const isToUser = userId === trade.toUserId;
    // Party check before any write — non-participants (incl. admins, whom
    // the rules allow) are read-only here. The switch is also disabled for
    // them; this is the backstop.
    if (!isFromUser && !isToUser) return;
    const userKey = isFromUser ? 'fromUser' : 'toUser';
    // Frozen once this user has paid: the charge was priced from the choice
    // at payment time. (Normally unreachable — the paid render short-circuits
    // to "Payment Completed!" — this is the backstop.)
    const paid = isFromUser
      ? trade.paymentStatus?.fromUserPaid
      : trade.paymentStatus?.toUserPaid;
    if (paid) return;
    userToggledProtectionRef.current = true;
    setIncludeProtection(checked);
    try {
      const tradeRef = doc(db, 'tradeOffers', trade.id);
      await updateDoc(tradeRef, {
        [`protection.${userKey}`]: checked,
        [`protectionDefaulted.${userKey}`]: false, // explicit choice now
        updatedAt: new Date()
      });
      setTrade((prev) => prev ? {
        ...prev,
        protection: { ...(prev.protection || {}), [userKey]: checked }
      } : prev);
      console.log('✅ Saved protection selection to trade document');
    } catch (err) {
      // The doc flag is what the server settles from — displaying a state we
      // failed to persist is exactly the mismatch the default-ON design must
      // never allow, in EITHER direction. Revert the UI to the last persisted
      // value and say so.
      console.error('❌ Error saving protection selection:', err);
      setIncludeProtection(!checked);
      alert('Could not save your TradeProtect choice — please try the toggle again.');
    }
  };

  const handleProceedToPayment = async () => {
    try {
      const userId = auth.currentUser?.uid;
      
      // Get Firebase Auth token
      const idToken = await auth.currentUser?.getIdToken();
      if (!idToken) {
        throw new Error('Authentication required');
      }

      // Validate cash amount
      const cashAmount = Math.abs(Number(trade.cashAmount || 0));
      if (isNaN(cashAmount) || cashAmount < 0) {
        throw new Error('Invalid cash amount in trade');
      }

      const baseUrl = 'https://us-central1-pedalxchange.cloudfunctions.net';

      const response = await fetch(`${baseUrl}/createStripeCheckout`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          tradeId: trade.id,
          protectionSelected: includeProtection,
          userId,
          applyCashCredit: applyCashCredit,
          appliedCredit: appliedCredit,
          returnTo: Capacitor.isNativePlatform() ? 'app' : undefined
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Payment setup failed');
      }

      const result = await response.json();

      // Server says this user already paid for this trade — no checkout to run,
      // just take them to progress (mirrors the purchase-side alreadyPaid handling).
      if (result.alreadyPaid) {
        console.log('ℹ️ Trade payment already completed — going to progress');
        navigate(`/trade-progress/${trade.id}`);
        return;
      }

      // Handle zero-payment referral credit path
      if (result.noPaymentRequired && result.referralCreditUsed) {
        console.log('✅ Referral credit applied — no Stripe payment needed');
        navigate(result.redirectUrl || `/trade-progress/${trade.id}`);
        return;
      }

      const url = result.url;

      if (url) {
        // Validate URL before redirect
        if (url.includes('stripe.com') || url.includes('checkout.stripe.com')) {
          // Native: in-app browser sheet. `onReturn` fires on ANY dismissal —
          // success AND cancel — and the webhook that writes the paid flag can
          // lag a few seconds. So poll for THIS user's paid flag: paid → trade
          // progress; never confirmed (cancelled, or webhook slower than the
          // poll window) → stay here so the user can retry. Navigating to
          // progress unpaid showed a misleading page mid-payment (F7).
          const uid = auth.currentUser?.uid;
          const paidField = uid === trade.fromUserId ? 'fromUserPaid' : 'toUserPaid';
          await openCheckout(url, {
            onReturn: () => {
              let attempts = 0;
              const maxAttempts = 6;
              const poll = async () => {
                attempts += 1;
                try {
                  const snap = await getDoc(doc(db, 'tradeOffers', trade.id));
                  if (snap.exists() && snap.data().paymentStatus?.[paidField]) {
                    navigate(`/trade-progress/${trade.id}`);
                    return;
                  }
                } catch (pollErr) {
                  console.warn('Post-checkout trade payment check failed:', pollErr);
                }
                if (attempts < maxAttempts) {
                  setTimeout(poll, 2000);
                } else {
                  console.log('ℹ️ Trade payment not confirmed after checkout sheet closed — staying on confirm');
                  setProcessingPayment(false);
                }
              };
              poll();
            }
          });
        } else {
          console.error('❌ Invalid redirect URL:', url);
          throw new Error('Invalid payment URL received');
        }
      } else {
        throw new Error('Missing Stripe URL in response.');
      }
    } catch (err) {
      console.error('❌ Payment setup error:', err);
      alert(`Payment setup failed: ${err.message}`);
    }
  };

  // Users receiving cash will return to trade-settlement, not here
  // This logic is no longer needed since onboarding returns go to settlement page

  // Auth still resolving (e.g. right after a pull-to-refresh reload) — show a
  // neutral loading state instead of prematurely deciding login/not-found.
  if (!authChecked) return <div className="p-4">Loading trade...</div>;

  if (!currentUser) {
    sessionStorage.setItem('returnAfterLogin', window.location.pathname);
    return (
      <div className="p-4 text-red-600">
        Please log in to view this trade.{' '}
        <a href="/login" style={{ color: '#2563eb', textDecoration: 'underline' }}>
          Click here to log in
        </a>
      </div>
    );
  }

  if (loading) return <div className="p-4">Loading trade...</div>;

  // Only after a completed fetch with a real user do we surface "not found".
  if (notFound) return <div className="p-4 text-gray-600">We couldn&apos;t find this trade. It may have been removed, or you may not have access to it.</div>;

  // Data still settling (transient null between fetches) — keep showing loading.
  if (!trade) return <div className="p-4">Loading trade...</div>;

  const userId = auth.currentUser?.uid;
  const isFromUser = userId === trade.fromUserId;
  // Non-parties (admins inspecting a trade) get a read-only page: the switch
  // is disabled and Continue is withheld — and the effects above make zero
  // writes for them (Jason's spec, 2026-09-18).
  const isTradeParty = userId === trade.fromUserId || userId === trade.toUserId;
  
  // Check if this user has already paid
  const currentUserPaid = isFromUser 
    ? trade.paymentStatus?.fromUserPaid 
    : trade.paymentStatus?.toUserPaid;

  // Calculate cash flow based on trade type
  const cash = Number(trade.cashAmount) || 0;
  let userPaysCash = false;
  let userReceivesCash = false;
  
  // 🔍 DEBUG: Log trade data to understand the issue
  console.log('🔍 TradeConfirm Debug:', {
    tradeId: trade.id,
    tradeType: trade.type,
    cashAmount: cash,
    userId,
    isFromUser,
    fromUserId: trade.fromUserId,
    toUserId: trade.toUserId
  });
  
  // Calculate who pays/receives cash based on trade type
  // MUST MATCH logic in createStripeCheckout.js
  
  // If cashAmount is 0, treat as straight trade (no cash flow)
  if (cash > 0) {
    // Prefer cashFlow fields (set during counter offers) for accuracy
    if (trade.cashFlow && trade.cashFlow.payer && trade.cashFlow.receiver) {
      userPaysCash = trade.cashFlow.payer === userId;
      userReceivesCash = trade.cashFlow.receiver === userId;
    } else {
      // Fallback for initial offers without cashFlow
      if (trade.type === 'ask') {
        // FromUser made an "ask" - toUser pays cash to fromUser
        userPaysCash = !isFromUser; // toUser pays
        userReceivesCash = isFromUser; // fromUser receives
      } else if (trade.type === 'offer') {
        // FromUser made an "offer" - fromUser pays cash to toUser
        userPaysCash = isFromUser; // fromUser pays
        userReceivesCash = !isFromUser; // toUser receives
      }
    }
  }
  // If cashAmount is 0, userPaysCash and userReceivesCash stay false (straight trade)
  
  // 🔍 DEBUG: Log payment calculation
  console.log('🔍 Payment Calculation:', {
    userPaysCash,
    userReceivesCash,
    cashAmount: userPaysCash ? cash : 0,
    receivedCash: userReceivesCash ? cash : 0,
    'trade.cashFlow': trade.cashFlow,
    'hasCashFlow': !!trade.cashFlow
  });

  // Calculate user's payment breakdown using membership benefits
  const isCurrentUserInitiator = auth.currentUser?.uid === trade.fromUserId;
  const myPedalCount = isCurrentUserInitiator ? getInitiatorPedalIds(trade).length : 1;
  let platformFee = getTradeCompletionFee(myPedalCount);
  
  // Check for referral credit — waive platform fee if user has credits
  const hasReferralCredit = platformFee > 0 && (firestoreUser?.referralCredits || 0) > 0;
  if (hasReferralCredit) {
    platformFee = 0;
  }
  
  // 🔍 DEBUG: Log membership tier and fee calculation
  console.log('🔍 Membership Debug:', {
    'firestoreUser?.membershipTier': firestoreUser?.membershipTier,
    'firestoreUser exists': !!firestoreUser,
    platformFee,
    'getTradeCompletionFee()': getTradeCompletionFee(myPedalCount),
    hasReferralCredit,
    referralCredits: firestoreUser?.referralCredits
  });
  const protectionFee = includeProtection ? getTradeProtectFee() : 0; // Dynamic based on membership
  const cashAmount = userPaysCash ? cash : 0;
  const cashFeePercentage = getCashFeePercentage() / 100; // Convert percentage to decimal
const cashFee = cashAmount > 0 ? Number((cashAmount * cashFeePercentage).toFixed(2)) : 0;
  const receivedCash = userReceivesCash ? cash : 0;
  
  // Calculate total fees (platform + protection + cash + cash fee)
  const totalFees = platformFee + protectionFee + cashAmount + cashFee;
  
  // Apply cash credit if user chooses to
  const appliedCredit = applyCashCredit ? Math.min(receivedCash, totalFees) : 0;
  const finalAmount = Math.max(0, totalFees - appliedCredit);

  // NET cash this user actually pockets after applying received cash to fees.
  // Shared formula (tradeUtils) so the button label, the onboarding redirect in
  // checkOnboardingAndProceed, AND the TradeSettlement gate all agree. Gating on
  // NET (not gross "is a receiver") avoids sending users to payout onboarding
  // when their cash is fully consumed by fees (net $0) — they pocket nothing.
  const netCashReceived = getNetCashReceived({
    grossReceived: receivedCash,
    totalFees,
    applyCashCredit
  });
  const needsPayoutSetup = userNeedsPayoutOnboarding({
    netCashReceived,
    stripeOnboardingComplete: firestoreUser?.stripeOnboardingComplete
  });

  // Calculate how much of the received cash PedalxChange keeps
  const pedalxchangeKeeps = applyCashCredit ? appliedCredit : 0;

  // If user has already paid, show different content
  if (currentUserPaid) {
    return (
      <div className="p-4 max-w-xl mx-auto bg-white rounded shadow">
        <h2 className="text-2xl font-bold mb-4 text-green-600">Payment Completed!</h2>
        <p className="mb-4 text-gray-700">
          ✅ You have already completed your payment for this trade.
        </p>
        <p className="mb-4 text-gray-600">
          You can now proceed to the trade progress page to continue with the next steps.
        </p>
        <button
          onClick={() => navigate(`/trade-progress/${trade.id}`)}
          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded"
        >
          Go to Trade Progress
        </button>
      </div>
    );
  }

  // Get pedal information for trade info text
  const initiatorSnapshots = 
    (trade.pedalSnapshots?.initiatorPedals && Array.isArray(trade.pedalSnapshots.initiatorPedals))
      ? trade.pedalSnapshots.initiatorPedals
      : (trade.pedalSnapshots?.initiatorPedal ? [trade.pedalSnapshots.initiatorPedal] : []);
  const responderSnapshot = trade.pedalSnapshots?.responderPedal || {};

  const formatPedalName = (pedal) => 
    (pedal.make && pedal.model) ? `${pedal.make} ${pedal.model}` : null;

  const formatPedalList = (pedals, fallback) => {
    const names = pedals.map(formatPedalName).filter(Boolean);
    if (names.length === 0) return fallback;
    if (names.length === 1) return names[0];
    if (names.length === 2) return `${names[0]} and ${names[1]}`;
    return names.slice(0, -1).join(', ') + ', and ' + names[names.length - 1];
  };

  const myPedalNames = isFromUser
    ? formatPedalList(initiatorSnapshots, 'your pedal')
    : formatPedalName(responderSnapshot) || 'your pedal';
  const theirPedalNames = isFromUser
    ? formatPedalName(responderSnapshot) || 'their pedal'
    : formatPedalList(initiatorSnapshots, 'their pedal');
  const isMultiPedalTrade = initiatorSnapshots.length > 1;
  const otherUserName = isFromUser ? usernames.toUser || 'another user' : usernames.fromUser || 'another user';

  // Savings banner: shown at the checkout moment, to TRADE PARTICIPANTS only
  // (admins can open this page read-only to inspect a trade — congratulating
  // them on someone else's fees would be wrong, and would burn the marker
  // under their uid). Keyed per trade + user in localStorage so it can never
  // repeat for this deal, even across visits. It appears nowhere else — the
  // progress pages deliberately do not mount it (repetition dulls it).
  const isTradeParticipant =
    currentUser?.uid === trade?.fromUserId || currentUser?.uid === trade?.toUserId;
  const savingsOwnValue = isFromUser
    ? initiatorSnapshots.reduce((sum, p) => sum + (Number(p?.price) || 0), 0)
    : (Number(responderSnapshot?.price) || 0);
  const savingsReceivingCount = isFromUser ? 1 : initiatorSnapshots.length;
  const savingsDollars = marketplaceFeeSavingsDollars(savingsOwnValue);

  return (
    <div className="trade-confirm-outer" style={{ backgroundColor: '#F7F3EB', minHeight: 'calc(100vh - var(--navbar-height, 125px))', display: 'flex', flexDirection: 'column', padding: '1.5rem', boxSizing: 'border-box', overflowX: 'hidden' }}>
      {isTradeParticipant && (
        <SavingsBanner
          storageKey={`trade:${trade.id}:${currentUser.uid}`}
          amountDollars={savingsDollars}
          ownLabel={!isFromUser || initiatorSnapshots.length <= 1 ? 'your pedal' : 'your pedals'}
          receivingLabel={savingsReceivingCount > 1 ? 'these' : 'this one'}
          variant="trade"
        />
      )}
      <style jsx>{`
        .single-card-container {
          box-sizing: border-box;
        }
        @media (max-width: 768px) {
          .trade-confirm-outer {
            padding: 0.75rem !important;
          }
          .single-card-container {
            padding: 0.75rem !important;
            border-radius: 8px !important;
            margin: 0 !important;
            max-width: 100% !important;
          }
          .trade-confirm-outer h2 {
            font-size: 1.5rem !important;
            margin-bottom: 1rem !important;
            padding: 0 0.25rem !important;
          }
          .trade-info-text {
            font-size: 0.9375rem !important;
            padding: 0 !important;
          }
          .tradeprotect-buttons {
            flex-direction: column !important;
            gap: 0.75rem !important;
          }
          .tradeprotect-buttons button {
            width: 100% !important;
            min-width: 0 !important;
            padding: 10px 16px !important;
          }
          .user-data-loading {
            margin: 0 0 1rem 0 !important;
            max-width: 100% !important;
          }
        }
      `}</style>
      
      <h2 className="text-2xl font-bold mb-4 text-center">Confirm Your Trade</h2>

      {/* User Data Loading Indicator */}
      {userDataLoading && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded user-data-loading" style={{ maxWidth: '900px', margin: '0 auto 1rem auto', width: '100%', boxSizing: 'border-box' }}>
          <p className="text-sm text-blue-800">
            ⏳ <strong>Loading account information...</strong> Please wait while we verify your identity and payment status.
          </p>
        </div>
      )}

      {/* Single Card Container */}
      <div className="single-card-container" style={{ 
        maxWidth: '900px', 
        margin: '0 auto', 
        width: '100%',
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '2rem',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        boxSizing: 'border-box'
      }}>

        {/* 1. Trade Information */}
        <div style={{ marginBottom: '2rem', paddingBottom: '2rem', borderBottom: '1px solid #e5e7eb' }}>
          <p className="text-gray-700 trade-info-text" style={{ fontSize: '1.125rem', lineHeight: '1.6', textAlign: 'center' }}>
            You are confirming and agreeing to trade {isMultiPedalTrade ? 'your' : 'a'} <strong>{myPedalNames}</strong> {isMultiPedalTrade ? 'for' : 'and'} <strong>{theirPedalNames}</strong> with <strong>{otherUserName}</strong>.
      </p>
        </div>

        {/* 2. Optional Trade Protect Section */}
        <div style={{ marginBottom: '2rem', paddingBottom: '2rem', borderBottom: '1px solid #e5e7eb' }}>
          <h4 className="font-semibold mb-3" style={{ fontSize: '1.25rem', color: '#C9A227' }}>Optional Protection</h4>
          
          <div className="mb-3" style={{ marginBottom: '1.5rem' }}>
            <div className="font-medium mb-2">Add TradeProtect for ${getTradeProtectFee().toFixed(2)}</div>
            <p className="text-xs text-gray-600 mb-2">
              Up to $250 reimbursement for eligible fraud or misrepresentation claims, not related to shipping damage, loss, theft, or carrier delays.
            </p>
            <button
              onClick={() => setShowDisclaimerModal(true)}
              className="text-xs hover:underline"
              style={{
                // Absolute, not relative: this button has no authored size (its
                // text-xs class is inert Tailwind), and a calc(1em + 1pt) bump
                // compounded with mobile Safari's font boosting into way more
                // than 1pt. 15px = the inherited 14px + 1pt, everywhere.
                fontSize: '15px',
                color: '#5A9ECB',
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                fontFamily: 'Nunito, sans-serif'
              }}
            >
              Read full disclaimer and terms
            </button>
          </div>

          {/* TradeProtect toggle — defaults ON (2026-09-18): the fee is in
              the totals until the user flicks it off. Replaces the previous
              Accept/Decline button pair and its choose-to-continue gate. */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem' }}>
            <button
              type="button"
              role="switch"
              aria-checked={includeProtection}
              disabled={!isTradeParty}
              onClick={() => handleProtectionToggle(!includeProtection)}
              style={{
                position: 'relative',
                width: '52px',
                height: '30px',
                borderRadius: '9999px',
                border: 'none',
                padding: 0,
                cursor: isTradeParty ? 'pointer' : 'not-allowed',
                backgroundColor: includeProtection ? '#C9A227' : '#D1D5DB',
                opacity: isTradeParty ? 1 : 0.6,
                transition: 'background-color 0.2s',
                // Invisible until the rehydrate/default effect has resolved the
                // initial position — deterministic replacement for the old
                // 100ms transition-arming timer (the knob appears in place
                // instead of flicking; animations then apply only to taps).
                visibility: protectionHydrated ? 'visible' : 'hidden',
                flexShrink: 0
              }}
            >
              <span style={{
                position: 'absolute',
                top: '3px',
                left: includeProtection ? '25px' : '3px',
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                backgroundColor: 'white',
                boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                transition: 'left 0.2s'
              }} />
            </button>
            <span style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 700, fontSize: '15px', visibility: protectionHydrated ? 'visible' : 'hidden', color: includeProtection ? '#C9A227' : '#6B7280' }}>
              {!isTradeParty
                ? (includeProtection ? 'TradeProtect added (view only)' : 'TradeProtect declined (view only)')
                : (includeProtection ? 'TradeProtect added' : 'TradeProtect declined')}
            </span>
          </div>

        </div>

        {/* 3. Full Payment Breakdown */}
        <div style={{ marginBottom: '2rem', background: '#fafafa', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '1.25rem', fontFamily: 'Nunito' }}>
          <h4 style={{ fontSize: '1.1rem', fontWeight: '700', color: '#1f2937', marginBottom: '1rem' }}>Payment breakdown</h4>

          <div style={{ fontSize: '0.7rem', fontWeight: '700', color: '#9ca3af', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: '0.5rem' }}>Fees</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem', fontSize: '0.9rem' }}>
            <span style={{ color: '#374151' }}>Platform fee</span>
            <span style={{ fontWeight: '600', color: '#374151' }}>
              {hasReferralCredit ? (
                <span style={{ color: '#22c55e' }}>$0.00 <span style={{ fontSize: '0.75rem' }}>(Referral credit)</span></span>
              ) : (
                <span>
                  ${platformFee.toFixed(2)}
                  {myPedalCount > 1 && (
                    <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}> ($3 + $1/addtl pedal)</span>
                  )}
                </span>
              )}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem', fontSize: '0.9rem' }}>
            <span style={{ color: '#374151' }}>TradeProtect fee</span>
            <span style={{ fontWeight: '600', color: '#374151' }}>${protectionFee.toFixed(2)}</span>
          </div>

          {cashAmount > 0 && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem', fontSize: '0.9rem' }}>
                <span style={{ color: '#374151' }}>Cash payment</span>
                <span style={{ fontWeight: '600', color: '#374151' }}>${cashAmount.toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem', fontSize: '0.9rem' }}>
                <span style={{ color: '#374151' }}>Cash processing fee ({getCashFeePercentage()}%)</span>
                <span style={{ fontWeight: '600', color: '#374151' }}>${cashFee.toFixed(2)}</span>
              </div>
            </>
          )}

          {receivedCash > 0 && (
            <>
              <div style={{ borderTop: '1px solid #e5e7eb', margin: '0.75rem 0', paddingTop: '0.75rem' }}>
                <div style={{ fontSize: '0.7rem', fontWeight: '700', color: '#9ca3af', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: '0.5rem' }}>Cash you're receiving</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem', fontSize: '0.9rem' }}>
                  <span style={{ color: '#374151' }}>Cash from other user</span>
                  <span style={{ fontWeight: '600', color: '#15803d' }}>${receivedCash.toFixed(2)}</span>
                </div>
              </div>

              {totalFees > 0 && (
                <>
                  <div style={{ background: '#f5f5f4', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.75rem', marginBottom: '0.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer', gap: '0.75rem' }} onClick={() => setApplyCashCredit(!applyCashCredit)}>
                      <div style={{
                        width: '44px', minWidth: '44px', height: '24px', borderRadius: '12px',
                        backgroundColor: applyCashCredit ? '#5A9ECB' : '#d1d5db',
                        position: 'relative', transition: 'background-color 0.2s', marginTop: '2px'
                      }}>
                        <div style={{
                          width: '20px', height: '20px', borderRadius: '50%', backgroundColor: 'white',
                          position: 'absolute', top: '2px', left: applyCashCredit ? '22px' : '2px',
                          transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                        }} />
                      </div>
                      <div style={{ textAlign: 'left' }}>
                        <span style={{ fontWeight: '600', fontSize: '0.85rem', color: '#1f2937' }}>Apply cash towards fees</span>
                        <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '2px' }}>
                          PedalxChange keeps the applied amount, you receive the remainder.
                        </p>
                      </div>
                    </div>
                  </div>

                  {applyCashCredit && (
                    <div style={{ paddingLeft: '0.25rem' }}>
                      {appliedCredit > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem', fontSize: '0.85rem' }}>
                          <span style={{ color: '#6b7280' }}>Applied to fees</span>
                          <span style={{ fontWeight: '600', color: '#15803d' }}>-${appliedCredit.toFixed(2)}</span>
                        </div>
                      )}
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem', fontSize: '0.85rem' }}>
                        <span style={{ color: '#6b7280' }}>Total you'll receive</span>
                        <span style={{ fontWeight: '600', color: '#15803d' }}>${(receivedCash - appliedCredit).toFixed(2)}</span>
                      </div>
                    </div>
                  )}
                </>
              )}

              {totalFees === 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem', fontSize: '0.85rem' }}>
                  <span style={{ color: '#6b7280' }}>Total you'll receive</span>
                  <span style={{ fontWeight: '600', color: '#15803d' }}>${receivedCash.toFixed(2)}</span>
                </div>
              )}
            </>
          )}

          <div style={{ borderTop: '2px solid #d1d5db', marginTop: '0.75rem', paddingTop: '0.75rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontWeight: '700', fontSize: '1rem', color: '#1f2937' }}>Final amount due</span>
            <span style={{ fontWeight: '700', fontSize: '1.1rem', color: finalAmount > 0 ? '#b91c1c' : '#15803d' }}>
              ${finalAmount.toFixed(2)}
            </span>
          </div>
        </div>

        {/* 4. Payment Button */}
            <div>
        {finalAmount > 0 ? (
          <>
            {needsPayoutSetup && (
              <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '10px', padding: '14px 16px', marginBottom: '1rem', textAlign: 'left', maxWidth: '420px', marginLeft: 'auto', marginRight: 'auto' }}>
                <p style={{ margin: 0, fontSize: '0.9rem', color: '#9a3412', lineHeight: '1.5' }}>
                  <strong>You're receiving ${receivedCash.toFixed(2)} from this trade.</strong> To get paid, you'll set up a one-time, free, secure payout account with Stripe.
                </p>
              </div>
            )}
            <div className="flex justify-center">
            <button
              onClick={checkOnboardingAndProceed}
                disabled={processingPayment || userDataLoading || !protectionHydrated || !isTradeParty}
                className="hover:opacity-80 transition-opacity"
                style={{
                  backgroundColor: (processingPayment || userDataLoading || !protectionHydrated || !isTradeParty) ? '#9CA3AF' : '#C9A227',
                  color: (processingPayment || userDataLoading || !protectionHydrated || !isTradeParty) ? '#E5E7EB' : 'white',
                  border: 'none',
                  borderRadius: '9999px',
                  padding: '12px 24px',
                  fontFamily: 'Nunito, sans-serif',
                  fontWeight: 'bold',
                  fontSize: '16px',
                  cursor: (processingPayment || userDataLoading || !protectionHydrated || !isTradeParty) ? 'not-allowed' : 'pointer',
                  maxWidth: '300px',
                  width: '100%'
                }}
            >
              {processingPayment ? 'Processing Payment...' : 
               userDataLoading ? 'Loading account information...' :
               needsPayoutSetup ? `Set up payout to receive $${receivedCash.toFixed(2)}` :
               `Proceed to Payment ($${finalAmount.toFixed(2)})`}
            </button>
          </div>
          </>
        ) : (
            <div className="text-center">
              <div style={{ background: '#e8f5f0', border: '1px solid #b2dfdb', borderRadius: '8px', padding: '0.75rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <div style={{ width: '22px', height: '22px', borderRadius: '50%', backgroundColor: '#4db6ac', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M2.5 6L5 8.5L9.5 3.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <span style={{ fontSize: '0.85rem', color: '#2e7d6e' }}>
                  <strong>No payment required!</strong>{' '}
                  {receivedCash > 0 
                    ? "You'll receive cash when the other user pays."
                    : "You don't owe any money for this trade."
                  }
                </span>
              </div>
            {needsPayoutSetup && (
              <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '10px', padding: '14px 16px', marginBottom: '1rem', textAlign: 'left', maxWidth: '420px', marginLeft: 'auto', marginRight: 'auto' }}>
                <p style={{ margin: 0, fontSize: '0.9rem', color: '#9a3412', lineHeight: '1.5' }}>
                  <strong>You're receiving ${receivedCash.toFixed(2)} from this trade.</strong> To get paid, you'll set up a one-time, free, secure payout account with Stripe.
                </p>
              </div>
            )}
            <button
              onClick={checkOnboardingAndProceed}
                disabled={processingPayment || userDataLoading || !protectionHydrated || !isTradeParty}
                className="hover:opacity-80 transition-opacity"
                style={{
                  backgroundColor: (processingPayment || userDataLoading || !protectionHydrated || !isTradeParty) ? '#9CA3AF' : '#C9A227',
                  color: (processingPayment || userDataLoading || !protectionHydrated || !isTradeParty) ? '#E5E7EB' : 'white',
                  border: 'none',
                  borderRadius: '9999px',
                  padding: '12px 24px',
                  fontFamily: 'Nunito, sans-serif',
                  fontWeight: 'bold',
                  fontSize: '16px',
                  cursor: (processingPayment || userDataLoading || !protectionHydrated || !isTradeParty) ? 'not-allowed' : 'pointer',
                  maxWidth: '300px',
                  width: '100%'
                }}
            >
              {processingPayment ? 'Processing...' : 
               userDataLoading ? 'Loading account information...' :
                 needsPayoutSetup ? `Set up payout to receive $${receivedCash.toFixed(2)}` :
                 'Continue to Trade Settlement'}
            </button>
          </div>
        )}
        </div>
      </div>

      {/* Disclaimer Modal */}
      {showDisclaimerModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem'
          }}
          onClick={() => setShowDisclaimerModal(false)}
        >
          <div 
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '2rem',
              paddingBottom: '3rem',
              maxWidth: '600px',
              maxHeight: '80vh',
              overflowY: 'auto',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-bold text-xl mb-4" style={{ color: '#C9A227', textAlign: 'left' }}>Important Disclaimer & Terms</h3>
            
            <div className="space-y-4" style={{ fontSize: '0.875rem', lineHeight: '1.6', color: '#4A5759', textAlign: 'left' }}>
              <p>
                You are trading directly with another user, not with PedalxChange.com, LLC ("PedalxChange"). Online trading involves inherent risks, including fraud, misrepresentation, theft, damage, shipping issues, and other problems that may result in financial loss.
              </p>

              <div>
                <h4 className="font-semibold mb-2" style={{ color: '#C9A227' }}>Nature of TradeProtect</h4>
                <ul className="ml-4 space-y-2" style={{ listStyleType: 'disc' }}>
                  <li>TradeProtect is an optional good‑will reimbursement program offered by PedalxChange for certain proven fraudulent or fraudulently misrepresented trades or transactions only.</li>
                  <li>TradeProtect is not an insurance policy, does not create an insurance contract, and does not make PedalxChange an insurer or insurance producer.</li>
                  <li>TradeProtect does not cover shipping damage, wear and tear, buyer's remorse, preference changes, normal transaction risk, or any non‑fraud‑related issues.</li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold mb-2" style={{ color: '#C9A227' }}>Claims, Investigation, and Discretion</h4>
                <ul className="ml-4 space-y-2" style={{ listStyleType: 'disc' }}>
                  <li>TradeProtect may provide up to $250 in good‑will reimbursement per eligible incident in PedalxChange's sole and absolute discretion, after review of the claim and supporting evidence.</li>
                  <li>All claims are subject to investigation, and PedalxChange may request cooperation, documentation, communications, photos, police reports, and any other evidence it deems necessary.</li>
                  <li>PedalxChange does not guarantee approval of any claim or any particular reimbursement amount; claims may be approved in full, approved in part, or denied entirely, for any reason or no stated reason.</li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold mb-2" style={{ color: '#C9A227' }}>User Responsibilities and Conduct</h4>
                <ul className="ml-4 space-y-2" style={{ listStyleType: 'disc' }}>
                  <li>You are expected to act in good faith, comply with our Terms of Service, and ship the exact item listed, in the described condition, within the agreed timeframe.</li>
                  <li>Users who send broken, incorrect, counterfeit, or fraudulent gear, or who attempt to defraud other users or PedalxChange, may have their accounts suspended or terminated and may be subject to further legal action.</li>
                  <li>You are responsible for complying with all applicable laws, including any tax, customs, and reporting obligations related to your trades.</li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold mb-2" style={{ color: '#C9A227' }}>Limitation of Liability and Release</h4>
                <p className="mb-2">
                  PedalxChange is an online platform that facilitates connections between users but is not a party to your trades.
                </p>
                <p className="mb-2">
                  To the maximum extent permitted by law, PedalxChange and its owners, officers, employees, and agents are not responsible or liable for:
                </p>
                <ul className="ml-4 space-y-2 mb-3" style={{ listStyleType: 'disc' }}>
                  <li>The outcome of any trade or transaction between users.</li>
                  <li>Any fraud, theft, misrepresentation, or deception by users.</li>
                  <li>Any lost, stolen, delayed, damaged, or destroyed property, gear, or money (including shipping issues, even where separate shipping coverage exists).</li>
                  <li>Any payment disputes, chargebacks, failed payments, or reversals.</li>
                  <li>Any indirect, incidental, consequential, special, or punitive damages, including lost profits, loss of use, or loss of goodwill, arising from or related to use of the platform or any trade.</li>
                </ul>
                <p className="mb-2 font-semibold">
                  By proceeding with payment, you acknowledge and agree that:
                </p>
                <ol className="ml-4 space-y-2" style={{ listStyleType: 'decimal' }}>
                  <li>You are trading directly, and at your own risk, with another user and not with PedalxChange.</li>
                  <li>PedalxChange may provide safety and verification tools but cannot prevent all fraud or misconduct.</li>
                  <li>Any TradeProtect reimbursement is not guaranteed, is not insurance, and is provided, if at all, purely as a discretionary good‑will payment by PedalxChange.</li>
                  <li>PedalxChange is not liable for trade outcomes, disputes, or any losses you may incur in connection with your use of the platform.</li>
                  <li>You release and agree to hold harmless and not sue PedalxChange (and its owners, officers, employees, and agents) from any and all claims, demands, causes of action, damages, losses, costs, and expenses arising out of or related to your trades, except to the limited extent such release is prohibited by applicable law.</li>
                </ol>
              </div>

              <p className="text-xs text-gray-500 mt-4">
                For full terms and conditions governing your use of PedalxChange and TradeProtect, please review our Terms of Service and Privacy Policy, which are incorporated by reference into this disclaimer.
              </p>
            </div>

            <div className="mt-6 flex justify-center" style={{ marginBottom: '4rem' }}>
              <button
                onClick={() => setShowDisclaimerModal(false)}
                className="hover:opacity-80 transition-opacity"
                style={{
                  backgroundColor: '#C9A227',
                  color: 'white',
                  border: 'none',
                  borderRadius: '9999px',
                  padding: '10px 24px',
                  fontFamily: 'Nunito, sans-serif',
                  fontWeight: 'bold',
                  fontSize: '15px',
                  cursor: 'pointer'
                }}
              >
                I Understand
              </button>
            </div>
          </div>
        </div>
      )}



      {/* Fixed gap below content */}
      <div style={{ height: '60px' }}></div>
      
      {/* Footer */}
      <div className="py-4 trade-confirm-footer" style={{ paddingBottom: '10px', backgroundColor: '#F7F3EB' }}>
        <style jsx>{`
          @media (max-width: 768px) {
            .trade-confirm-footer {
              padding-bottom: 80px !important;
            }
          }
        `}</style>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-sm text-gray-600">
              Need help or found a bug?{' '}
              <button 
                onClick={() => openContact({ category: 'support' })}
                className="hover:underline"
                style={{ 
                  color: '#5A9ECB',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  font: 'inherit',
                  cursor: 'pointer'
                }}
              >
                Contact PXC Support
              </button>
            </p>
          </div>
        </div>
      </div>

      <PayoutAccountLinkedModal
        isOpen={!!payoutLinkedNotice}
        reconnected={payoutLinkedNotice?.reconnected}
        onClose={() => {
          const next = payoutLinkedNotice?.onContinue;
          setPayoutLinkedNotice(null);
          if (next) next();
        }}
      />
    </div>
  );
};

export default TradeConfirm;

